/* ==========================================================================
   Floating Header — Overkicks
   Custom elements:
     <announcement-ticker>  RAF marquee (ported from announcement-slider)
     <floating-header>      sticky/fixed + height compensation
     <floating-mega-menu>   desktop hover/keyboard mega panel + L2→L3 switching
   The mobile drawer reuses Dawn's <header-drawer> (global.js) unchanged.
   ========================================================================== */

const FH_REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const FH_DESKTOP = window.matchMedia('(min-width: 990px)');

/* -------------------------------------------------------------------------
   Announcement ticker
   ------------------------------------------------------------------------- */
class AnnouncementTicker extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.fh-ticker__track');
    if (!this.track) return;
    this.speed = parseFloat(this.dataset.speed) || 1;
    this.offset = 0;
    this.paused = false;
    this.rafId = null;

    if (FH_REDUCED_MOTION.matches) return; // static, no animation

    // Defer measuring until layout is ready (fonts/images may shift width).
    requestAnimationFrame(() => this.start());
  }

  start() {
    this.fillToCover();
    this.firstSetWidth = this.originalSetWidth;
    if (!this.firstSetWidth) return;

    this.onEnter = () => (this.paused = true);
    this.onLeave = () => (this.paused = false);
    this.onTouchStart = () => (this.paused = true);
    this.onTouchEnd = () => {
      this.touchTimer = setTimeout(() => (this.paused = false), 1000);
    };

    this.addEventListener('mouseenter', this.onEnter);
    this.addEventListener('mouseleave', this.onLeave);
    this.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.addEventListener('touchend', this.onTouchEnd, { passive: true });

    this.rafId = requestAnimationFrame(this.tick.bind(this));
  }

  // Clone the original item set until the track is at least 2× the container
  // width, so the seamless reset never reveals an empty gap.
  fillToCover() {
    const containerWidth = this.offsetWidth;
    this.originalSetWidth = this.track.scrollWidth;
    if (this.originalSetWidth === 0) return;

    const originals = Array.from(this.track.children);
    let total = this.originalSetWidth;
    let guard = 0;
    while (total < containerWidth * 2 && guard < 50) {
      originals.forEach((node) => {
        const clone = node.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        this.track.appendChild(clone);
      });
      total += this.originalSetWidth;
      guard += 1;
    }
  }

  tick() {
    if (!this.paused) {
      this.offset += this.speed;
      if (this.offset >= this.firstSetWidth) {
        this.offset -= this.firstSetWidth;
      }
      this.track.style.transform = `translateX(${-this.offset}px)`;
    }
    this.rafId = requestAnimationFrame(this.tick.bind(this));
  }

  disconnectedCallback() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    if (this.touchTimer) clearTimeout(this.touchTimer);
  }
}
customElements.define('announcement-ticker', AnnouncementTicker);

/* -------------------------------------------------------------------------
   Floating header — sticky/fixed + content height compensation
   ------------------------------------------------------------------------- */
class FloatingHeader extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('.floating-header__bar');
    this.isFixed = this.dataset.fixed === 'true';
    this.stickyMode = this.dataset.sticky || 'on-scroll-up';
    this.lastY = 0;

    this.setHeights();

    this.resizeObserver = new ResizeObserver(() => this.setHeights());
    this.resizeObserver.observe(this);

    this.onMediaChange = () => this.setHeights();
    FH_DESKTOP.addEventListener('change', this.onMediaChange);

    if (this.isFixed) {
      document.body.classList.add('fh-fixed');
      if (this.stickyMode === 'on-scroll-up') {
        this.onScroll = this.throttle(this.handleScroll.bind(this), 50);
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
    }

    // Theme editor: recompute on section reload.
    document.addEventListener('shopify:section:load', this.onMediaChange);
    document.addEventListener('shopify:section:unload', this.onMediaChange);
  }

  setHeights() {
    const total = this.offsetHeight;
    document.documentElement.style.setProperty('--fh-total-height', `${total}px`);
    if (this.bar) {
      const bottom = Math.round(this.bar.getBoundingClientRect().bottom);
      // Used by Dawn's HeaderDrawer height math (reused for the mobile drawer).
      document.documentElement.style.setProperty('--header-bottom-position', `${bottom}px`);
    }
  }

  handleScroll() {
    const y = window.scrollY || document.documentElement.scrollTop;
    const past = y > this.offsetHeight;
    if (y > this.lastY && past) {
      this.classList.add('fh-hidden');
    } else if (y < this.lastY) {
      this.classList.remove('fh-hidden');
    }
    this.lastY = y;
  }

  throttle(fn, wait) {
    let last = 0;
    let timer = null;
    return (...args) => {
      const now = Date.now();
      const remaining = wait - (now - last);
      if (remaining <= 0) {
        last = now;
        fn(...args);
      } else if (!timer) {
        timer = setTimeout(() => {
          last = Date.now();
          timer = null;
          fn(...args);
        }, remaining);
      }
    };
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    FH_DESKTOP.removeEventListener('change', this.onMediaChange);
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('shopify:section:load', this.onMediaChange);
    document.removeEventListener('shopify:section:unload', this.onMediaChange);
  }
}
customElements.define('floating-header', FloatingHeader);

/* -------------------------------------------------------------------------
   Mega menu — one L1 trigger + its panel
   ------------------------------------------------------------------------- */
class FloatingMegaMenu extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('.fh-l1__trigger');
    this.panel = this.querySelector('.fh-mega');
    if (!this.panel || !this.trigger) return;

    this.closeTimer = null;

    this.addEventListener('mouseenter', () => this.scheduleOpen());
    this.addEventListener('mouseleave', () => this.scheduleClose());
    this.trigger.addEventListener('focus', () => this.open());
    this.addEventListener('keyup', (e) => {
      if (e.code && e.code.toUpperCase() === 'ESCAPE') {
        this.close();
        this.trigger.focus();
      }
    });
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.close();
    });

    // L2 → L3 switching
    this.l2Triggers = Array.from(this.querySelectorAll('.fh-mega__l2-trigger'));
    this.l2Triggers.forEach((l2) => {
      const target = l2.dataset.target;
      if (!target) return;
      l2.addEventListener('mouseenter', () => this.activateL2(l2, target));
      l2.addEventListener('focus', () => this.activateL2(l2, target));
    });
  }

  scheduleOpen() {
    if (!FH_DESKTOP.matches) return;
    clearTimeout(this.closeTimer);
    this.open();
  }

  scheduleClose() {
    if (!FH_DESKTOP.matches) return;
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.close(), 120);
  }

  open() {
    if (!FH_DESKTOP.matches) return;
    this.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  activateL2(l2El, targetId) {
    this.querySelectorAll('.fh-mega__l2').forEach((el) => el.classList.remove('is-active'));
    const li = l2El.closest('.fh-mega__l2');
    if (li) li.classList.add('is-active');

    this.querySelectorAll('.fh-mega__l3-panel').forEach((panel) => {
      panel.hidden = panel.id !== targetId;
    });
  }
}
customElements.define('floating-mega-menu', FloatingMegaMenu);
